/**
 * Utilitário para detecção e uso de imagens WebP com fallback
 */

import React, { forwardRef } from 'react';

// Verifica suporte a WebP uma vez e armazena o resultado
let webpSupportCache = null;

// Cache de verificação de existência de arquivo WebP
const webpExistsCache = new Map();

/**
 * Verifica se o navegador suporta WebP
 * @returns {Promise<boolean>} Promise que resolve para true se o navegador suportar WebP
 */
export function checkWebPSupport() {
  // Se já verificamos antes, retornamos o resultado em cache
  if (webpSupportCache !== null) {
    return Promise.resolve(webpSupportCache);
  }

  return new Promise(resolve => {
    const webpImg = new Image();
    
    webpImg.onload = function() {
      // Se a imagem carregou com largura maior que 0, WebP é suportado
      webpSupportCache = webpImg.width > 0;
      console.log('WebP support detected:', webpSupportCache);
      resolve(webpSupportCache);
    };
    
    webpImg.onerror = function() {
      // Se houve erro, WebP não é suportado
      webpSupportCache = false;
      console.log('WebP support not detected');
      resolve(false);
    };
    
    // Pequena imagem de teste WebP em base64
    webpImg.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
  });
}

/**
 * Verifica se um arquivo WebP existe realmente (para evitar 404)
 * @param {string} webpUrl - URL da imagem WebP
 * @returns {Promise<boolean>} - Promise que resolve para true se o arquivo existir
 */
export function checkWebPExists(webpUrl) {
  // Se já verificamos antes, retornamos o resultado em cache
  if (webpExistsCache.has(webpUrl)) {
    return Promise.resolve(webpExistsCache.get(webpUrl));
  }

  return new Promise(resolve => {
    const img = new Image();
    
    img.onload = function() {
      webpExistsCache.set(webpUrl, true);
      resolve(true);
    };
    
    img.onerror = function() {
      webpExistsCache.set(webpUrl, false);
      resolve(false);
    };
    
    img.src = webpUrl;
  });
}

/**
 * Hook React para retornar a URL da imagem otimizada com base no suporte do navegador
 * @param {string} originalUrl - URL da imagem original (PNG/JPG)
 * @returns {string} URL da imagem WebP se suportada, senão a URL original
 */
export function getOptimizedImageUrl(originalUrl) {
  // Verifica se a URL é uma string válida
  if (!originalUrl || typeof originalUrl !== 'string') {
    return originalUrl;
  }
  
  // Se for uma URL remota ou um SVG, retorna a URL original
  if (originalUrl.startsWith('http') || originalUrl.endsWith('.svg')) {
    return originalUrl;
  }
  
  // Calcula a URL WebP (remove a extensão e adiciona .webp)
  const webpUrl = originalUrl.replace(/\.(png|jpg|jpeg)$/i, '.webp');
  
  // Se a detecção de WebP já foi feita e é suportada, retorna a URL WebP
  if (webpSupportCache === true) {
    // Verificar se o arquivo WebP existe
    checkWebPExists(webpUrl).then(exists => {
      if (!exists) {
        console.log(`WebP file does not exist: ${webpUrl}`);
      }
    });
    return webpUrl;
  }
  
  // Se já sabemos que WebP não é suportado, retorna a URL original
  if (webpSupportCache === false) {
    return originalUrl;
  }
  
  // Se ainda não sabemos se WebP é suportado, retorna a URL original
  // (a verificação acontecerá em paralelo e afetará futuras chamadas)
  checkWebPSupport();
  return originalUrl;
}

/**
 * Componente React para exibir uma imagem com WebP e fallback
 * @param {Object} props - Propriedades do componente
 * @param {string} props.src - URL da imagem original
 * @param {string} props.alt - Texto alternativo
 * @param {Object} props.style - Estilos CSS
 * @param {string} props.className - Classes CSS
 * @returns {JSX.Element} Elemento de imagem otimizado
 */
export const OptimizedImage = forwardRef(({ src, alt, style, className, ...props }, ref) => {
  // URL padrão para webp (vazio se src for inválido)
  const webpUrl = src && typeof src === 'string' 
    ? src.replace(/\.(png|jpg|jpeg)$/i, '.webp')
    : '';
  
  // Hook useEffect - agora sendo chamado incondicionalmente
  React.useEffect(() => {
    if (webpUrl && !src.startsWith('http') && !src.endsWith('.svg')) {
      checkWebPExists(webpUrl).then(exists => {
        if (!exists) {
          console.log(`WebP file not found for Image component: ${webpUrl}`);
        }
      });
    }
  }, [webpUrl, src]);
  
  // Verificações condicionais após os hooks serem chamados
  // Verifica se a URL é válida
  if (!src || typeof src !== 'string') {
    return null;
  }
  
  // Se for SVG ou URL remota, renderiza diretamente
  if (src.startsWith('http') || src.endsWith('.svg')) {
    return <img ref={ref} src={src} alt={alt} style={style} className={className} {...props} />;
  }
  
  // Renderiza <picture> com WebP e fallback
  return (
    <picture>
      <source srcSet={webpUrl} type="image/webp" />
      <source srcSet={src} type="image/png" />
      <img ref={ref} src={src} alt={alt} style={style} className={className} {...props} />
    </picture>
  );
});

// Criar objeto WebpUtils
const WebpUtils = {
  checkWebPSupport,
  checkWebPExists,
  getOptimizedImageUrl,
  OptimizedImage
};

export default WebpUtils; 